import './styles/graphic.scss';
import * as d3 from 'd3';
import meta from './data/meta.json';
import houseDesktopSVG from './img/svg/house-desktop.svg';
import houseMobileSVG from './img/svg/house-mobile.svg';
import seesawSVG from './img/svg/seesaw.svg';

const CHART_TEXT_URL = 'https://ix.cnn.io/data/redistricting/redistricting-chart-text.json';
const STATES_DATA_URL = 'https://ix.cnn.io/data/redistricting/redistricting-states-data.json';
const CHART_SLUG = '20251022-redistricting-seesaw';

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
    // width,
    // height
  } = props;

  const containerDiv = container.append('div')
    .attr('class', 'container');

  // Fetch both chart text and states data
  Promise.all([
    d3.json(CHART_TEXT_URL),
    d3.json(STATES_DATA_URL),
  ]).then(([chartData, statesData]) => {
    // Find the data for this specific chart
    const chartText = chartData.find(item => item.chart_slug === CHART_SLUG);

    if (!chartText) {
      console.error('Chart text not found for slug:', CHART_SLUG);
      return;
    }

    // Calculate total dem and rep seats
    let totalDemSeats = 0;
    let totalRepSeats = 0;

    statesData.forEach((state) => {
      totalDemSeats += parseInt(state.dem_seats) || 0;
      totalRepSeats += parseInt(state.rep_seats) || 0;
    });

    const seatDifference = Math.abs(totalRepSeats - totalDemSeats);
    let headline = '';

    if (totalRepSeats > totalDemSeats) {
      headline = `Republicans could flip <span class="highlight rep">${seatDifference}</span> more seats than Democrats`;
    } else if (totalDemSeats > totalRepSeats) {
      headline = `Democrats could flip <span class="highlight dem">${seatDifference}</span> more seats than Republicans`;
    } else {
      headline = 'The parties are currently tied for number of seats flipped';
    }

    // Render the graphic with the calculated headline
    renderGraphic(chartText, headline, containerDiv, isMobile);
  }).catch((error) => {
    console.error('Error loading data:', error);
  });
}

function renderGraphic(chartText, headline, containerDiv, isMobile) {
  const seesawText = containerDiv.append('div')
    .attr('class', 'seesawText');

  seesawText
    .append('p')
    .text(meta.kicker)
    .attr('class', 'kicker');

  seesawText
    .append('h1')
    .html(meta.headline);

  seesawText.append('p')
    .attr('class', 'subhed')
    .html(meta.subhed);

  // Add legend
  const legend = seesawText.append('div')
    .attr('class', 'legend');

  legend.append('h3')
    .attr('class', 'legend-title')
    .text('Targeted House seats under newly enacted redistricting maps');

  const legendItems = legend.append('div')
    .attr('class', 'legend-items');

  // Enacted item
  const enactedItem = legendItems.append('div')
    .attr('class', 'legend-item');

  const enactedSymbol = enactedItem.append('div')
    .attr('class', 'legend-symbol');

  enactedSymbol.append('div')
    .attr('class', 'diamond diamond-red filled');

  enactedSymbol.append('div')
    .attr('class', 'diamond diamond-blue filled');

  enactedItem.append('span')
    .attr('class', 'legend-label')
    .text('Enacted');

  // Add seesaw SVG inline
  const seesawGraphicDiv = seesawText.append('div')
    .attr('class', 'seesawGraphic');

  // Helper function to send height (defined early for use in multiple places)
  const sendHeight = () => {
    if (window.pymChild) {
      window.pymChild.sendHeight();
    }
  };

  // Detect parent URL (with retry logic for Pym initialization)
  const detectParentUrl = () => {
    const checkParentUrl = () => {
      if (window.pymChild && window.pymChild.parentUrl) {
        console.log('Parent URL (from Pym):', window.pymChild.parentUrl);
        return window.pymChild.parentUrl;
      } if (document.referrer) {
        console.log('Parent URL (from referrer):', document.referrer);
        return document.referrer;
      }
      console.log('Parent URL: Not available (standalone mode)');
      return null;
    };

    // Try immediately
    let parentUrl = checkParentUrl();

    // If not available, try again after Pym initializes
    if (!parentUrl) {
      setTimeout(() => {
        parentUrl = checkParentUrl();
      }, 100);
      setTimeout(() => {
        parentUrl = checkParentUrl();
      }, 500);
    }

    return parentUrl;
  };

  const parentUrl = detectParentUrl();

  // Check if parent URL does NOT contain "state-redistricting-maps-vis" and adjust styling
  setTimeout(() => {
    const currentParentUrl = (window.pymChild && window.pymChild.parentUrl) || document.referrer;
    if (currentParentUrl && !currentParentUrl.includes('state-redistricting-maps-vis')) {
      // Inject a style tag to override CSS with high specificity
      const style = document.createElement('style');
      style.innerHTML = `
        body, #root {
          background-color: white !important;
        }
        .container {
          padding: 0 !important;
          box-shadow: none !important;
        }
      `;
      document.head.appendChild(style);
      console.log('Styling adjusted (not state-redistricting-maps-vis): white background, no container padding/shadow');
    }
  }, 600);

  // Fetch and insert SVG inline for CSS animation
  fetch(seesawSVG)
    .then(response => response.text())
    .then((svgContent) => {
      seesawGraphicDiv.html(svgContent);
      // Send height after SVG is loaded
      sendHeight();
      setTimeout(sendHeight, 100);
      setTimeout(sendHeight, 300);
    });

  const contextText = containerDiv.append('div')
    .attr('class', 'contextText');

  contextText
    .append('h2')
    .attr('class', 'contextHead')
    .text(meta.context_head);

  contextText
    .append('p')
    .attr('class', 'contextDek')
    .html(meta.context_dek);

  const contextGraphic = contextText
    .append('div')
    .attr('class', 'contextGraphic');

  // Add labels above the SVG
  const topLabels = contextGraphic.append('div')
    .attr('class', 'graphic-labels-top');

  topLabels.append('div')
    .attr('class', 'label-left')
    .text('2024 House election results');

  topLabels.append('div')
    .attr('class', 'label-center')
    .text('Majority');

  // Insert the house breakdown SVG
  const svgContainer = contextGraphic.append('div')
    .attr('class', 'svg-container');

  const houseImg = svgContainer.append('img')
    .attr('src', isMobile ? houseMobileSVG : houseDesktopSVG)
    .attr('alt', 'House breakdown visualization');

  // Add labels below the SVG
  const bottomLabels = contextGraphic.append('div')
    .attr('class', 'graphic-labels-bottom');

  bottomLabels.append('div')
    .attr('class', 'label-left')
    .text('Democratic seats');

  bottomLabels.append('div')
    .attr('class', 'label-right')
    .text('Republican seats');

  // Send height after initial render
  sendHeight();

  // Send height after house breakdown image loads
  houseImg.on('load', () => {
    sendHeight();
    // Extra delayed calls to catch any layout shifts
    setTimeout(sendHeight, 100);
    setTimeout(sendHeight, 500);
  });

  // Send height after a short delay (catch any async content)
  setTimeout(sendHeight, 200);
  setTimeout(sendHeight, 1000);
  setTimeout(sendHeight, 2000);

  // Set up MutationObserver to catch any DOM changes
  if (window.MutationObserver && window.pymChild) {
    const observer = new MutationObserver(() => {
      sendHeight();
    });

    observer.observe(containerDiv.node(), {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }
}
